import { __assign } from 'tslib';
import React__default, { useState, useEffect } from 'react';
import { KpkAutoCompleteType } from '@keepeek/commons';
import { useTheme, useMediaQuery, InputAdornment, CircularProgress, Button, ListSubheader } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import DefaultIcon from '../../DefaultIcon/index.js';
import KSplitButton from '../../KButtons/KSplitButton/index.js';
import { generateButtonSx } from '../../utils/buttonStyle.js';
import StyledKSearchInput, { classes } from './index.styled.js';

var optionsFocused = function (event) {
    var focused = false;
    // Case: Navigate in autocomplete options
    Array.from(document.getElementsByClassName("KSearchInputOption")).forEach(function (element) {
        if (element.hasAttribute("data-focus")) {
            focused = true;
        }
    });
    // Case: Selecting split buttons
    var splitButtonsDataSelectors = ["KSplitButton-Button", "KSplitButton-MenuItem"];
    if (splitButtonsDataSelectors.some(function (selector) { var _a; return (_a = document.querySelector("*:focus")) === null || _a === void 0 ? void 0 : _a.classList.contains(selector); })) {
        focused = true;
    }
    // Case: Select option in split button with keyboard (A11Y)
    if ((event === null || event === void 0 ? void 0 : event.target) &&
        splitButtonsDataSelectors.some(function (selector) {
            return event.target.classList.contains(selector);
        })) {
        focused = true;
    }
    return focused;
};
var KSearchInput = function KSearchInput(_a) {
    var className = _a.className, _b = _a.query, query = _b === void 0 ? "" : _b, _c = _a.setQuery, setQuery = _c === void 0 ? function () { } : _c, label = _a.label, backgroundColor = _a.backgroundColor, textColor = _a.textColor, buttonStyle = _a.buttonStyle, placeholder = _a.placeholder, buttonLabel = _a.buttonLabel, _d = _a.size, size = _d === void 0 ? "medium" : _d, loading = _a.loading, onSearch = _a.onSearch, onOptionSelected = _a.onOptionSelected, autoCompleteOptions = _a.autoCompleteOptions, splitValue = _a.splitValue, splitLabel = _a.splitLabel, splitValues = _a.splitValues, onSplitSelect = _a.onSplitSelect, onSplitOpen = _a.onSplitOpen, _e = _a.autoFocus, autoFocus = _e === void 0 ? false : _e, EditableSanitizeHtml = _a.EditableSanitizeHtml, isSearchButtonEmpty = _a.isSearchButtonEmpty, inputBorderRadius = _a.inputBorderRadius, _f = _a.KIcon, KIcon = _f === void 0 ? DefaultIcon : _f;
    var t = useTranslation("refront-components").t;
    var theme = useTheme();
    var isDownMD = useMediaQuery(theme.breakpoints.down("md"));
    var isDownSM = useMediaQuery(theme.breakpoints.down("sm"));
    var _g = useState(false), open = _g[0], setOpen = _g[1];
    var _h = useState(false), showAutocomplete = _h[0], setShowAutocomplete = _h[1];
    var _j = useState(null), options = _j[0], setOptions = _j[1];
    var _k = useState(splitValue || (splitValues && splitValues[0]) || undefined), currentSplitValue = _k[0], setCurrentSplitValue = _k[1];
    useEffect(function () {
        setCurrentSplitValue(splitValue || (splitValues && splitValues[0]) || undefined);
    }, [splitValue, splitValues]);
    useEffect(function () {
        setOptions(autoCompleteOptions || []);
        setShowAutocomplete(autoCompleteOptions ? (autoCompleteOptions === null || autoCompleteOptions === void 0 ? void 0 : autoCompleteOptions.length) > 0 : false);
    }, [autoCompleteOptions]);
    var renderGroup = function (params) {
        var groupLabel = t("components.searchInput.group.query.label");
        if (params.group === KpkAutoCompleteType.Folder) {
            groupLabel = t("components.searchInput.group.folder.label");
        }
        return [
            React__default.createElement(ListSubheader, { key: params.key, component: "div" }, groupLabel),
            params.children,
        ];
    };
    var searchInternal = function () {
        if (!optionsFocused()) {
            setOpen(false);
            setShowAutocomplete(false);
            onSearch(query, currentSplitValue);
        }
    };
    var handleSearchKeyDown = function (event) {
        if (event.key === "Enter" && !optionsFocused(event)) {
            searchInternal();
        }
    };
    var onInputChange = function (value, reason) {
        if (reason === "reset") {
            return;
        }
        setQuery(value);
    };
    return (React__default.createElement(Autocomplete, { classes: {
            listbox: clsx("KSearchInputListbox", classes.listbox),
            option: clsx("KSearchInputOption", classes.option),
            groupUl: classes.groupUl
        }, fullWidth: true, className: clsx("KSearchInput", className), open: open && showAutocomplete, onOpen: function () {
            setOpen(true);
            setShowAutocomplete(false);
        }, onClose: function () {
            setOpen(false);
            setShowAutocomplete(false);
        }, isOptionEqualToValue: function (option, value) {
            return option.id === value.id && option.type === value.type;
        }, getOptionLabel: function (option) { return option.label; }, options: options || [], groupBy: function (option) { return option.type; }, renderGroup: renderGroup, loading: loading, disableClearable: true, forcePopupIcon: false, size: "small", inputValue: query, onInputChange: function (_event, value, reason) { return onInputChange(value, reason); }, onChange: function (_event, value, reason) {
            if (reason === "selectOption") {
                setOpen(false);
                setShowAutocomplete(false);
                onOptionSelected(value);
            }
        }, filterOptions: function (options) { return options; }, renderInput: function (params) {
            var _a, _b;
            return (React__default.createElement(StyledKSearchInput, __assign({}, params, { backgroundColor: backgroundColor, textColor: textColor, onKeyDown: handleSearchKeyDown, id: "k-search-input", className: clsx((_a = {},
                    _a[classes.serchTextField] = currentSplitValue !== undefined &&
                        splitValues !== undefined &&
                        splitValues.length !== 0,
                    _a)), inputBorderRadius: inputBorderRadius, color: "primary", placeholder: placeholder, size: size, variant: "outlined", inputProps: __assign(__assign({}, params.inputProps), { "aria-label": label }), "data-testid": "searchInputField", InputProps: __assign(__assign({}, params.InputProps), { autoFocus: autoFocus, startAdornment: !isDownSM &&
                        currentSplitValue !== undefined &&
                        splitValues !== undefined &&
                        splitValues.length !== 0 && (React__default.createElement(KSplitButton, { backgroundColor: backgroundColor, textColor: textColor, value: currentSplitValue, splitLabel: splitLabel || "", splitValues: splitValues, onSplitSelect: function (value) {
                            onSplitSelect && onSplitSelect(value);
                            setCurrentSplitValue(value);
                        }, onSplitOpen: function (opened) {
                            onSplitOpen && onSplitOpen(opened);
                            if (opened) {
                                // hide autocomple when opening split button
                                setShowAutocomplete(false);
                            }
                        }, className: classes.splitButton, testId: "searchTypeSelector", KIcon: KIcon })), endAdornment: (React__default.createElement(InputAdornment, { position: "end" },
                        loading ? (React__default.createElement(CircularProgress, { className: classes.loader, color: "inherit", size: 20 })) : null,
                        React__default.createElement(Button, { variant: buttonLabel !== "" ? "outlined" : "text", color: "primary", "aria-label": t("components.searchInput.actions.search.label"), onClick: searchInternal, size: "small", "data-testid": "searchButton", sx: __assign(__assign(__assign(__assign(__assign(__assign({}, generateButtonSx({
                                buttonStyle: buttonStyle,
                                theme: theme
                            })), (isSearchButtonEmpty && {
                                border: 0,
                                "&:hover": { border: 0 },
                                borderRadius: "50%"
                            })), { padding: "3px 9px", minWidth: "inherit" }), (!isSearchButtonEmpty && {
                                ".MuiButton-startIcon": (_b = {},
                                    _b[theme.breakpoints.down("md")] = {
                                        marginRight: 0,
                                        marginLeft: 0
                                    },
                                    _b)
                            })), (isSearchButtonEmpty && {
                                ".MuiButton-iconSizeSmall": {
                                    marginRight: 0,
                                    marginLeft: 0
                                }
                            })), { gap: "8px" }) },
                            React__default.createElement(KIcon, { sx: { lineHeight: "inherit" }, className: "fa-solid fa-magnifying-glass fa-lg" }),
                            !isDownMD && EditableSanitizeHtml && (React__default.createElement(EditableSanitizeHtml, { hideIfEmpty: true, labelKey: buttonLabel })),
                            !isDownMD && !EditableSanitizeHtml && buttonLabel))) }) })));
        } }));
};

export { KSearchInput as default };
//# sourceMappingURL=index.js.map
